/**
 * Chat Memory Service — Conversation summaries for cross-session context
 *
 * On chat clear, generates a 1-sentence summary of the conversation
 * and stores it in localStorage. Prior summaries (rolling window of 3)
 * are included in the system prompt so the AI has continuity.
 *
 * Issue: YDMA-179 §3.2
 */

import { logError } from './logger';
import { geminiClient } from './geminiClient';
import type { ChatMessage, ChatMode } from './mediaChatService';

// ============================================================
// CONSTANTS
// ============================================================

const MEMORY_KEY_PREFIX = 'mi-chat-summaries';
const MAX_SUMMARIES = 3;

// ============================================================
// TYPES
// ============================================================

export interface ConversationSummary {
  /** ISO timestamp of when the conversation was cleared */
  timestamp: string;
  /** Chat mode the conversation was in */
  mode: ChatMode;
  /** One-sentence AI-generated summary */
  summary: string;
}

// ============================================================
// STORAGE
// ============================================================

/**
 * Keyed by mode AND scope, matching MediaChatBubble's transcript key.
 *
 * Scoping the visible transcript alone was not enough, and the half that was
 * missed is the half that reaches the model: these summaries are injected into
 * the prompt by buildPriorContextPrompt. Two pages in 'performance' mode — the
 * dashboard and /campaigns — would otherwise have one page's summarised
 * conversation, and its figures, argued back at the other page's context.
 *
 * Undefined scope keeps the original key, so existing memory survives.
 */
const getStorageKey = (mode: ChatMode, scope?: string): string =>
  scope ? `${MEMORY_KEY_PREFIX}-${mode}-${scope}` : `${MEMORY_KEY_PREFIX}-${mode}`;

/**
 * Loads prior conversation summaries (rolling window of MAX_SUMMARIES).
 */
export const getConversationSummaries = (mode: ChatMode, scope?: string): ConversationSummary[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(mode, scope));
    if (!raw) return [];
    const parsed: ConversationSummary[] = JSON.parse(raw);
    return parsed.slice(-MAX_SUMMARIES);
  } catch {
    return [];
  }
};

/**
 * Saves a new conversation summary, maintaining the rolling window.
 */
export const saveConversationSummary = (mode: ChatMode, summary: string, scope?: string): void => {
  try {
    const existing = getConversationSummaries(mode, scope);
    const entry: ConversationSummary = {
      timestamp: new Date().toISOString(),
      mode,
      summary,
    };
    const updated = [...existing, entry].slice(-MAX_SUMMARIES);
    localStorage.setItem(getStorageKey(mode, scope), JSON.stringify(updated));
  } catch (err) {
    logError('ChatMemory', 'Failed to save conversation summary', err);
  }
};

/**
 * Clears all stored summaries for a mode.
 */
export const clearConversationSummaries = (mode: ChatMode, scope?: string): void => {
  try {
    localStorage.removeItem(getStorageKey(mode, scope));
  } catch {
    // non-critical
  }
};

// ============================================================
// SUMMARY GENERATION
// ============================================================

/**
 * Generates a 1-sentence summary of a conversation using Gemini.
 * Falls back to a static summary if the AI call fails.
 *
 * This is called on chat clear — not blocking, best-effort.
 */
export const generateConversationSummary = async (
  mode: ChatMode,
  messages: ChatMessage[]
): Promise<string> => {
  if (!messages || messages.length === 0) return '';

  // Build a compact transcript
  const transcript = messages
    .filter((m) => m.text || m.content)
    .map((m) => {
      const role = m.role === 'user' ? 'User' : 'AI';
      const text = (m.text || m.content || '').slice(0, 200);
      return `${role}: ${text}`;
    })
    .join('\n');

  if (!transcript.trim()) return '';

  const modeLabel = mode === 'mmm' ? 'MMM Analysis' : 'Media Intelligence';
  const prompt = `Summarize the following ${modeLabel} conversation in ONE concise sentence (in Thai if the conversation is in Thai, otherwise English). Focus on what the user was asking about and the key insight or outcome.

CONVERSATION:
${transcript}

Respond with ONLY the summary sentence, no quotes or labels:`;

  try {
    const result = await geminiClient.chat({
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.3,
        topK: 20,
        topP: 0.8,
        maxOutputTokens: 150,
      },
    });
    return result.trim();
  } catch (err) {
    logError('ChatMemory', 'Gemini summary failed, using fallback', err);
    // Fallback: simple static summary from last user question
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUserMsg) {
      const text = (lastUserMsg.text || lastUserMsg.content || '').slice(0, 80);
      return `การสนทนาก่อนหน้า: ${text}`;
    }
    return 'การสนทนาก่อนหน้าเกี่ยวกับการวิเคราะห์ข้อมูลแคมเปญ';
  }
};

// ============================================================
// SYSTEM PROMPT BUILDER
// ============================================================

/**
 * Builds a "prior conversations" context string to prepend to system prompts.
 * Returns empty string if no summaries exist.
 *
 * Example output:
 * "คุณเคยสนทนากับผู้ใช้เกี่ยวกับ:
 * 1. วิเคราะห์ ROAS ของ Facebook Ads และพบว่าต้องลดงบ
 * 2. เปรียบเทียบ CPL ระหว่าง Google และ TikTok"
 */
export const buildPriorContextPrompt = (mode: ChatMode, scope?: string): string => {
  const summaries = getConversationSummaries(mode, scope);
  if (summaries.length === 0) return '';

  const lines = summaries.map((s, idx) => `${idx + 1}. ${s.summary}`).join('\n');
  return `\n\nPRIOR CONVERSATION CONTEXT (last ${summaries.length} conversation(s)):
${lines}

Use this context to maintain continuity if the user references prior discussions. Do not explicitly mention these summaries unless asked.`;
};

export default {
  getConversationSummaries,
  saveConversationSummary,
  clearConversationSummaries,
  generateConversationSummary,
  buildPriorContextPrompt,
};
